import { computed } from 'vue';
import type { RatingValue } from '../components/RatingEmoji.types';

/**
 * Which set of icons + fill behaviour the CSAT rating strip uses.
 * - `emoji`: per-value png that swaps to a gif on the single selected option (scaled up).
 * - `stars`: shared star svg where every option up to and including the selected one is filled.
 */
export type RatingDisplayMode = 'emoji' | 'stars';

/**
 * Consistent shape for both modes: the image to show when an option is unselected vs. selected.
 */
interface RatingImage {
  default: string;
  selected: string;
}

/**
 * Emoji assets. Using require() for webpack bundling so assets are processed at build time.
 */
const emojiIcons: Record<RatingValue, RatingImage> = {
  1: { default: require('@/assets/img/rating/unhappy.png'), selected: require('@/assets/img/rating/unhappy.gif') },
  2: {
    default: require('@/assets/img/rating/unsatisfied.png'),
    selected: require('@/assets/img/rating/unsatisfied.gif'),
  },
  3: { default: require('@/assets/img/rating/neutral.png'), selected: require('@/assets/img/rating/neutral.gif') },
  4: { default: require('@/assets/img/rating/satisfied.png'), selected: require('@/assets/img/rating/satisfied.gif') },
  5: { default: require('@/assets/img/rating/happy.png'), selected: require('@/assets/img/rating/happy.gif') },
};

/**
 * Star assets, shared across all rating values.
 */
const starIcons: RatingImage = {
  default: require('@/assets/img/star-unclicked.svg'),
  selected: require('@/assets/img/star-clicked.svg'),
};

export interface RatingDisplayConfig {
  /** Resolves the icon src for a rating value given whether that option is shown as selected. */
  resolveImageSrc: (value: RatingValue, isSelected: boolean) => string;
  /** When true, every option with value <= selected rating appears selected (stars). */
  cumulativeSelection: boolean;
  /** When true, the selected icon scales up (emoji flair). */
  animateSelection: boolean;
}

const RATING_DISPLAY_CONFIG: Record<RatingDisplayMode, RatingDisplayConfig> = {
  emoji: {
    resolveImageSrc: (value, isSelected) => emojiIcons[value][isSelected ? 'selected' : 'default'],
    cumulativeSelection: false,
    animateSelection: true,
  },
  stars: {
    resolveImageSrc: (_value, isSelected) => starIcons[isSelected ? 'selected' : 'default'],
    cumulativeSelection: true,
    animateSelection: false,
  },
};

/**
 * The active display mode. Hardcoded for now; this is the single seam where customisation
 * (e.g. a PUI field getter) gets wired in later without touching the rating components.
 */
const CURRENT_RATING_DISPLAY_MODE: RatingDisplayMode = 'stars';

/**
 * Exposes the current rating display mode and its config as reactive refs.
 */
export const useRatingDisplay = () => {
  const mode = computed(() => CURRENT_RATING_DISPLAY_MODE);
  const config = computed(() => RATING_DISPLAY_CONFIG[mode.value]);

  return { mode, config };
};
